//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';

interface ContextWindowData {
  used: number;
  max: number;
  pct: number;
  model: string;
  estimated?: boolean;
}

interface ContextWindowRingProps {
  data: ContextWindowData | null;
}

const SIZE = 34;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function ringColor(pct: number): string {
  if (pct >= 85) return '#F44336';
  if (pct >= 60) return '#FF9800';
  return '#4CAF50';
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

const ContextWindowRing: React.FC<ContextWindowRingProps> = ({ data }) => {
  if (!data) return null;

  const { used, max, pct, model, estimated } = data;
  const clampedPct = Math.min(100, Math.max(0, pct));
  const dashOffset = CIRCUMFERENCE * (1 - clampedPct / 100);
  const color = ringColor(clampedPct);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            style={{
              flexShrink: 0,
              cursor: 'default',
              opacity: 1,
              animation: 'fadeInRing 0.3s ease-in',
              display: 'block',
            }}
            aria-label={`Context window: ${pct}% full`}
          >
            <style>{`
              @keyframes fadeInRing { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
            {/* Track */}
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="rgba(128,128,128,0.2)"
              strokeWidth={STROKE}
            />
            {/* Progress arc */}
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              style={{
                transition: 'stroke-dashoffset 0.4s ease, stroke 0.3s ease',
              }}
            />
            {/* Percentage label */}
            <text
              x={SIZE / 2}
              y={SIZE / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="8"
              fontWeight="600"
              fill={color}
              style={{ userSelect: 'none' }}
            >
              {Math.round(clampedPct)}%
            </text>
          </svg>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs leading-relaxed">
          <span>
            Context: {formatNumber(used)} / {formatNumber(max)} tokens ({pct}%)
            {estimated && <> (estimated)</>}
            <br />
            Model: {model || 'unknown'}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ContextWindowRing;
